import { Mail, MapPin, User, ArrowUpRight, Shield } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n/I18nProvider';
import { legal, COMPANY } from '@/i18n/legal';
import { CONTACT } from '@/constants/testIds';
import LegalPageShell from '@/components/site/LegalPageShell';

export default function ContactPage() {
  const { locale } = useI18n();
  const copy = legal[locale].contact;

  return (
    <LegalPageShell
      testId={CONTACT.root}
      titleTestId={CONTACT.title}
      backTestId={CONTACT.backHome}
      eyebrow={copy.eyebrow}
      title={copy.title}
      lead={copy.lead}
    >
      <div className="grid sm:grid-cols-2 gap-5">
        {/* Address card */}
        <Card
          data-testid={CONTACT.addressCard}
          className="bg-card border border-border rounded-2xl p-6 sm:p-7"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {copy.addressTitle}
          </div>
          <div className="mt-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary shrink-0">
              <MapPin className="h-4 w-4" />
            </div>
            <div className="text-sm leading-relaxed">
              <div className="font-display text-base text-foreground">
                {COMPANY.legalName}
              </div>
              <div className="mt-1 text-muted-foreground">
                {COMPANY.street}
                <br />
                {COMPANY.city}
                <br />
                {COMPANY.country[locale]}
              </div>
            </div>
          </div>
          <div className="mt-5 flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary shrink-0">
              <User className="h-4 w-4" />
            </div>
            <div className="text-sm leading-relaxed">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {copy.responsibleLabel}
              </div>
              <div className="mt-0.5 text-foreground">{COMPANY.responsible}</div>
            </div>
          </div>
        </Card>

        {/* General enquiries card */}
        <Card className="bg-card border border-border rounded-2xl p-6 sm:p-7 flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {copy.generalTitle}
          </div>
          <div className="mt-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary shrink-0">
              <Mail className="h-4 w-4" />
            </div>
            <div className="text-sm leading-relaxed">
              <a
                href={`mailto:${COMPANY.email}`}
                className="text-foreground hover:text-primary transition-colors"
              >
                {COMPANY.email}
              </a>
            </div>
          </div>
          <Button
            asChild
            data-testid={CONTACT.emailBtn}
            className="mt-6 h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 self-start"
          >
            <a href={`mailto:${COMPANY.email}`}>
              {copy.writeEmail}
              <ArrowUpRight className="ml-1 h-4 w-4" />
            </a>
          </Button>
        </Card>
      </div>

      {/* Data protection card — full width */}
      <Card className="bg-card border border-border rounded-2xl p-6 sm:p-7 mt-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg border border-border bg-secondary flex items-center justify-center text-primary shrink-0">
            <Shield className="h-4 w-4" />
          </div>
          <div className="text-sm leading-relaxed">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {copy.privacyTitle}
            </div>
            <p className="mt-2 text-muted-foreground">
              {copy.privacyBody}
              <a
                data-testid={CONTACT.privacyEmailBtn}
                href={`mailto:${COMPANY.privacyEmail}`}
                className="text-foreground hover:text-primary transition-colors underline underline-offset-4"
              >
                {COMPANY.privacyEmail}
              </a>
              .
            </p>
            <Link
              to="/privacy"
              className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              {legal[locale].privacy.title}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </Card>
    </LegalPageShell>
  );
}
